import { IncidentHistoricalControllerService } from "../../../services/incident-historical-controller.service";
import { ReferenceNameControllerService } from "../../../services/reference-name-controller.service";
import { InternalUserControllerService } from "../../../services/internal-user-controller.service";
import { AnalyticsControllerService } from "../../../services/analytics-controller.service";
import { WebTrackingInitService } from "../../../services/web-tracking-init.service";
import { RightContainerTypeControllerService } from "../../../services/right-container-types-controller.service";
import { UserRightsService } from "../../../services/user-rights.service";

import { AppStore } from "../../../stores/app-store.service";

import { ContainerTypeDTO } from "../../../models/container-type";
import { readColorStatusSelection } from "../../../models/color-status-filter";

import { SimpleDialogComponent } from "../../dialog/simple-dialog.component";
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTabNav, MatTabLink, MatTabNavPanel } from "@angular/material/tabs";
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet
} from "@angular/router";
import { FilterUrlSyncService } from "../../../services/filter-url-sync.service";
import { LANDING_SEGMENT, TAB_SEGMENTS, TabDefinition } from "../../../tabs";
import { Subscription, filter } from "rxjs";
import { SvgIconComponent } from "angular-svg-icon";
import { NgStyle, NgTemplateOutlet } from "@angular/common";
import { MatTooltip } from "@angular/material/tooltip";
import { LangManagerComponent } from "../../utils/lang-manager/lang-manager.component";
import { TranslateModule } from "@ngx-translate/core";

/**
 * Application shell. Was the SPA's MatTabGroup host; now a router-driven header
 * (mat-tab-nav-bar) over a `<router-outlet>`. It keeps the cross-cutting concerns
 * that used to live here — status/container-type store seeding, the per-feature
 * loading indicators, the analytics session timeout, language + logout — while
 * the feature pages mount/unmount through routing.
 */
@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./dashboard.component.scss"],
  imports: [
    SvgIconComponent,
    NgStyle,
    NgTemplateOutlet,
    MatTooltip,
    MatTabNav,
    MatTabLink,
    MatTabNavPanel,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LangManagerComponent,
    TranslateModule
  ]
})
export class DashboardComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  ifIE = false;
  login: string;
  userName: string;

  interval: number;

  managerLoading: boolean | undefined;
  mapLoading: number | undefined;
  protected analyticsRemainingTime: number | undefined;
  protected hoverLoadingMap: string;
  protected displayTooltipAndCloseForAnalytics: boolean = false;
  protected textAnalyticsTooltip: string;

  /** The top-bar tabs, in order — one `<a mat-tab-link>` each. */
  protected readonly tabs: readonly TabDefinition[] = TAB_SEGMENTS;

  /** First path segment of the active route, e.g. "kpi", "map", "analytics". */
  protected currentSegment: string = LANDING_SEGMENT;

  /**
   * Query params each top-bar tab link carries, keyed by target segment. The URL
   * is authoritative for filters (see FilterUrlSyncService), so a bare routerLink
   * would land on the target with an empty query string and switch every filter
   * off; each link instead passes the subset of the current params that its own
   * target page reads. A tab that declares no `filterConfig` has no entry, and
   * `[queryParams]="undefined"` leaves its link bare — same as not binding it.
   */
  protected tabQueryParams: { [segment: string]: Params } = {};

  /**
   * Where each top-bar tab link points, keyed by target segment. Usually the tab's
   * bare root - but between two segments that share a `recordShape` (src/app/tabs.ts)
   * the record sub-path comes along, so leaving the map for the list keeps the site
   * or the tracker you were looking at (`/map/site/88` -> `/list/site/88`) instead of
   * dropping you on the fleet and making you find it again.
   */
  protected tabLinks: { [segment: string]: unknown[] } = {};

  private timeoutAnalyticsTab: number = 30; // minutes before the analytics tab times out and is destroyed

  constructor(
    private _router: Router,
    private _userService: InternalUserControllerService,
    private _userRightsService: UserRightsService,
    public dialog: MatDialog,
    private store: AppStore,
    public referenceNameControllerService: ReferenceNameControllerService,
    public incidentHistoricalControllerService: IncidentHistoricalControllerService,
    public analyticsControllerService: AnalyticsControllerService,
    public rightContainerTypeControllerService: RightContainerTypeControllerService,
    private webTrackingInitService: WebTrackingInitService,
    private _route: ActivatedRoute,
    private filterSync: FilterUrlSyncService
  ) {
    const ua = window.navigator.userAgent;
    if (ua.indexOf("MSIE ") > 0 || ua.indexOf("Trident/") > 0) {
      this.ifIE = true;
    }
    this.currentSegment = this.topSegment(this._router.url);
    this.refreshTabQueryParams();
    this.refreshTabLinks(this._router.url);
  }

  ngOnInit() {
    this.login = localStorage.getItem("initials");
    this.userName = localStorage.getItem("username");
    this._userService.setIsLogin(true);

    this.subscription.add(
      this._router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => this.onNavigationEnd(e.urlAfterRedirects))
    );

    this.subscription.add(
      this.store.ctTypes$.subscribe(() => {
        // keep the store subscription alive (container types seeded below)
      })
    );

    this.subscription.add(
      this.referenceNameControllerService
        .getIsLoading()
        .subscribe((isLoading) => {
          this.managerLoading = isLoading;
          if (
            this.managerLoading === false &&
            this.currentSegment === "manager"
          ) {
            this.referenceNameControllerService.setIsLoading(undefined);
          }
        })
    );

    this.subscription.add(
      this.incidentHistoricalControllerService
        .getIsLoading()
        .subscribe((loading) => {
          this.mapLoading = loading;
          this.hoverLoadingMap =
            loading === -1 ? "Timeout error" : `Loading : ${loading} %`;
          if (this.mapLoading === 100 && this.currentSegment === "map") {
            this.incidentHistoricalControllerService.setIsLoading(undefined);
          }
        })
    );

    this.subscription.add(
      this.analyticsControllerService
        .getRemainingTime()
        .subscribe((remainingTime) => {
          this.analyticsRemainingTime = remainingTime;
          this.displayTooltipAndCloseForAnalytics =
            this.analyticsRemainingTime !== undefined &&
            this.analyticsRemainingTime > 0;
          if (this.displayTooltipAndCloseForAnalytics)
            this.textAnalyticsTooltip = `Remaining time :<br> ${this.analyticsRemainingTime} min`;
          if (this.analyticsRemainingTime === 0) {
            this.analyticsRemainingTime = undefined;
            this.analyticsControllerService.setRemainingTime(undefined);
          }
        })
    );

    this.initStatus();
    this.initContainerTypes();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    clearInterval(this.interval);
  }

  /**
   * Whether this user can reach a tab. A tab with no `requiredRole` is open to
   * every signed-in user; one that declares a role defers to the same check
   * `roleGuard` makes on the route from the same table entry, so a hidden tab and
   * a refused URL can no longer disagree.
   * @param tab {TabDefinition} The tab being rendered.
   * @returns {boolean} True when the link should be shown.
   */
  protected canReach(tab: TabDefinition): boolean {
    return (
      !tab.requiredRole ||
      this._userRightsService.getUserRightLevel(tab.requiredRole)
    );
  }

  initStatus() {
    this.store.setStatus(readColorStatusSelection());
  }

  initContainerTypes() {
    let ctTypes = new Array<ContainerTypeDTO>();
    this.subscription.add(
      this.rightContainerTypeControllerService
        .getContainerTypesAuthorized(this.userName)
        .subscribe((result) => {
          result.map((res) => {
            ctTypes.push(
              new ContainerTypeDTO({
                code: res.code,
                category: res.category,
                label: res.label
              })
            );
          });
          const updatedCtTypes = ctTypes.map((ctType) => ({
            ...ctType,
            checked: result.some((res) => res.code === ctType.code)
          }));
          this.store.setTypes(updatedCtTypes);
        })
    );
  }

  logoutClicked() {
    const dialogRef = this.dialog.open(SimpleDialogComponent, {
      data: {
        title: "Log out",
        text: "Are you sure you want to logout ?",
        yes: "Yes",
        no: "No"
      }
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.webTrackingInitService.stop();
          this._userService.logout();
          clearInterval(this.interval);
          this._router.navigate(["/"], {});
        }
      })
    );
  }

  /** Track a nav click through the web-tracking service. */
  trackTabClick(trackingId: string): void {
    const trackingService = (this.webTrackingInitService as any)
      ?.webTrackingService;
    trackingService?.trackClick?.(trackingId);
  }

  /** Close/kill the analytics session from the nav-bar affordance. */
  onCloseAnalytics() {
    this.trackTabClick("button-close-analytics");
    this.analyticsControllerService.setRemainingTime(undefined);
  }

  /**
   * React to route changes: update the active segment, clear the transient
   * per-feature loading flags on entry, and drive the analytics session timeout
   * (previously handled in selectedTabChange).
   */
  private onNavigationEnd(url: string): void {
    const previous = this.currentSegment;
    const current = this.topSegment(url);
    this.currentSegment = current;
    // Before the previous === current early-return below: a filter change is a
    // query-param-only navigation, and the tab links have to pick it up too. So is
    // opening a record without leaving the tab, which is what refreshTabLinks reads.
    this.refreshTabQueryParams();
    this.refreshTabLinks(url);

    if (current === "manager" && this.managerLoading === false) {
      this.referenceNameControllerService.setIsLoading(undefined);
    }
    if (
      current === "map" &&
      (this.mapLoading === 100 || this.mapLoading === -1)
    ) {
      this.incidentHistoricalControllerService.setIsLoading(undefined);
    }

    if (previous === current) {
      return;
    }
    // Left analytics with cached data → start the eviction countdown.
    if (previous === "analytics" && this.analyticsRemainingTime === -1) {
      this.analyticsControllerService.setRemainingTime(
        this.timeoutAnalyticsTab
      );
    }
    // Re-entered analytics within the allowed window → pause the countdown.
    else if (current === "analytics" && this.analyticsRemainingTime >= 0) {
      this.analyticsControllerService.setRemainingTime(-1);
    }
  }

  /**
   * Recompute what each tab link carries from the params currently in the URL:
   * the intersection with the target page's own config, so a param it never reads
   * is not dragged along. Every `paramKey` is globally unique (see
   * filter-sync-configs.ts), so that intersection is all the filtering a hop
   * needs — a key means the same filter on both sides or is simply not carried.
   */
  private refreshTabQueryParams(): void {
    const params: { [segment: string]: Params } = {};
    for (const tab of this.tabs) {
      if (tab.filterConfig) {
        params[tab.segment] = this.filterSync.carriedParams(
          this._route,
          tab.filterConfig
        );
      }
    }
    this.tabQueryParams = params;
  }

  /**
   * Recompute where each tab link points. A tab that shares the current tab's
   * `recordShape` gets the record sub-path appended; every other tab, and every URL
   * that is not on a record, gets the bare root - which is what the links were
   * before this existed.
   * @param url {string} The URL the app has just landed on.
   */
  private refreshTabLinks(url: string): void {
    const suffix: string[] = this.recordSuffix(url);
    const from: TabDefinition = this.tabs.find(
      (tab) => tab.segment === this.topSegment(url)
    );
    const links: { [segment: string]: unknown[] } = {};
    for (const tab of this.tabs) {
      const carries: boolean =
        suffix.length > 0 &&
        !!tab.recordShape &&
        tab.recordShape === from?.recordShape;
      links[tab.segment] = carries
        ? ["/" + tab.segment, ...suffix]
        : ["/" + tab.segment];
    }
    this.tabLinks = links;
  }

  /**
   * The `record/id` pair under the top segment (`site/88`), or `[]` when the URL
   * addresses no record. Three segments is the shape every record URL in the app
   * has under a fleet tab; anything else is a page, not a record.
   */
  private recordSuffix(url: string): string[] {
    const segments: string[] = this.pathSegments(url);
    return segments.length === 3 ? segments.slice(1) : [];
  }

  private topSegment(url: string): string {
    return this.pathSegments(url)[0] ?? LANDING_SEGMENT;
  }

  /**
   * The URL's path segments, without its query string, fragment or empty parts.
   *
   * Decoded, because `Router.url` is the encoded URL while a `routerLink` command
   * array is the DECODED form the router encodes on its way out. Handing a segment
   * back as it came would encode it twice: a site code carries spaces, so
   * `/map/site/98763A%20%2001` produced a `/list/site/98763A%2520%252001` link that
   * then failed to resolve to any site.
   */
  private pathSegments(url: string): string[] {
    return url
      .split(/[?#]/)[0]
      .split("/")
      .filter((segment) => segment.length > 0)
      .map((segment) => decodeURIComponent(segment));
  }
}
